import ChecklistIcon from '@mui/icons-material/Checklist'
import { Badge, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material'
import PropTypes from 'prop-types'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import useOnboardingPlan from '@/features/dashboard/setup/useOnboardingPlan'
import useAuth from '@/hooks/useAuth'
import { ORG_SETUP_ROLES } from '@/lib/roles'
import { hasRole } from '@/utils/hasRole'
import { showSetupNudge } from './stepDisplay'

/**
 * Sidebar entry for the setup hub with a remaining-steps badge (org-onboarding-hub
 * task 6.3). Self-contained rather than a navConfig entry because it is the one nav item
 * whose visibility depends on per-org state: rendered only for administrators of an org
 * whose setup is not yet marked complete, and gone for everyone else — the hub itself
 * stays reachable at /setup after completion, just not advertised here.
 */
export default function SetupNavItem({ collapsed }) {
    const { activeOrg, user } = useAuth()
    const location = useLocation()

    const eligible = showSetupNudge(activeOrg, hasRole(user, ORG_SETUP_ROLES))
    const { data: plan } = useOnboardingPlan(activeOrg?.id, { enabled: eligible })

    if (!eligible) {
        return null
    }

    const remaining = plan ? Math.max(0, plan.progress.total - plan.progress.done) : null

    return (
        <Tooltip title={collapsed ? 'Setup' : ''} placement="right">
            <ListItemButton
                component={RouterLink}
                to="/setup"
                selected={location.pathname === '/setup'}
                sx={{
                    minHeight: 48,
                    px: 2.5,
                    justifyContent: collapsed ? 'center' : 'flex-start'
                }}
                aria-label="Setup"
            >
                <ListItemIcon
                    sx={{
                        minWidth: 0,
                        mr: collapsed ? 0 : 2,
                        justifyContent: 'center'
                    }}
                >
                    <Badge badgeContent={remaining} color="warning" max={99}>
                        <ChecklistIcon />
                    </Badge>
                </ListItemIcon>
                {!collapsed && <ListItemText primary="Setup" />}
            </ListItemButton>
        </Tooltip>
    )
}

SetupNavItem.propTypes = {
    collapsed: PropTypes.bool
}
